import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Trash2, UserPlus } from "lucide-react";
import { ModuleHeader } from "./AdminUI";
import { errMsg, fieldCls, labelCls } from "./adminShared";

function AddAdminForm({ onAdded }) {
    const [f, setF] = useState({ name: "", email: "", password: "" });
    const submit = async (e) => {
        e.preventDefault();
        try {
            await client.post("/admin/users", f);
            toast.success(`${f.email} can now sign in`);
            setF({ name: "", email: "", password: "" });
            onAdded();
        } catch (err) {
            toast.error(errMsg(err, "Could not add admin"));
        }
    };
    return (
        <form onSubmit={submit} className="border border-brand-line rounded-sm p-6 bg-brand-charcoal h-fit">
            <h2 className="font-display text-xl">Add an admin</h2>
            <div className="space-y-3 mt-4">
                <div><label className={labelCls}>Name</label><input data-testid="user-new-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={fieldCls} /></div>
                <div><label className={labelCls}>Email</label><input data-testid="user-new-email" required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={fieldCls} /></div>
                <div><label className={labelCls}>Password (min 8 characters)</label><input data-testid="user-new-password" required minLength={8} type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={fieldCls} /></div>
            </div>
            <button data-testid="user-add-btn" className="btn-primary mt-5 text-sm inline-flex items-center gap-2"><UserPlus size={14} /> Add admin</button>
        </form>
    );
}

function AdminRow({ u, isMe, onChanged }) {
    const [pw, setPw] = useState("");
    const [open, setOpen] = useState(false);

    const changePw = async () => {
        try {
            await client.patch(`/admin/users/${encodeURIComponent(u.email)}/password`, { password: pw });
            toast.success("Password updated");
            setPw("");
            setOpen(false);
        } catch (err) {
            toast.error(errMsg(err, "Could not update password"));
        }
    };
    const remove = async () => {
        if (!window.confirm(`Remove ${u.email}? They will no longer be able to sign in.`)) return;
        try {
            await client.delete(`/admin/users/${encodeURIComponent(u.email)}`);
            toast.success("Admin removed");
            onChanged();
        } catch (err) {
            toast.error(errMsg(err, "Could not remove admin"));
        }
    };

    return (
        <div data-testid={`user-row-${u.email}`} className="px-5 py-4 border-t border-brand-line first:border-t-0">
            <div className="flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[180px]">
                    <p className="text-brand-cream">{u.name || u.email.split("@")[0]} {isMe && <span className="text-[10px] uppercase tracking-wider text-brand-gold ml-2">You</span>}</p>
                    <p className="text-xs text-brand-mute">{u.email}</p>
                </div>
                <button data-testid={`user-change-pw-${u.email}`} onClick={() => setOpen((v) => !v)} className="btn-ghost text-xs inline-flex items-center gap-1.5 !px-3 !py-2"><KeyRound size={12} /> Change password</button>
                {!isMe && <button data-testid={`user-delete-${u.email}`} onClick={remove} className="p-2 border border-brand-line hover:border-destructive hover:text-destructive rounded-sm"><Trash2 size={13} /></button>}
            </div>
            {open && (
                <div className="flex gap-2 mt-3">
                    <input data-testid={`user-new-pw-${u.email}`} type="password" minLength={8} placeholder="New password (min 8)" value={pw} onChange={(e) => setPw(e.target.value)} className={`${fieldCls} !mt-0 max-w-xs`} />
                    <button data-testid={`user-save-pw-${u.email}`} disabled={pw.length < 8} onClick={changePw} className="btn-primary text-xs disabled:opacity-40">Save</button>
                </div>
            )}
        </div>
    );
}

export default function UsersModule({ me }) {
    const [users, setUsers] = useState([]);
    const load = () => client.get("/admin/users").then((r) => setUsers(r.data));
    useEffect(() => { load(); }, []);

    return (
        <div data-testid="admin-users-module">
            <ModuleHeader eyebrow="Access" title="Admin Users" subtitle="Everyone listed here can sign in to this dashboard. You can't remove your own account." />
            <div className="grid lg:grid-cols-[1fr_360px] gap-6">
                <div className="border border-brand-line rounded-sm bg-brand-charcoal h-fit">
                    {users.map((u) => <AdminRow key={u.email} u={u} isMe={u.email === me?.email} onChanged={load} />)}
                </div>
                <AddAdminForm onAdded={load} />
            </div>
        </div>
    );
}
